import React, { useEffect, useRef, useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import DateFilterDropdown from "aldoo-ra/DateFilterDropdown"
import Tabs from "aldoo-ra/Tabs"
import TextField from "aldoo-ra/TextField"
import { TrashIcon, PencilSquareIcon } from "@heroicons/react/24/outline"
import Modal from "aldoo-ra/Modal"
import appConfig from "aldoo-ra/Config"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import { APIKeysPlatformAPI } from "aldoo-ra/Admin/PlatformAccess/api-keys-api"
import { showMessageDialog } from "aldoo-ra/MessageDialog"
import { readComponentUptime, readComponentLatency, readComponentHistory, listChecks, testCheckNow, updateComponent, deleteCheck } from "../api"
import { useStatusUpdates } from "../realtime"
import HealthGrid from "../Dashboard/HealthGrid"
import CheckEditor from "../Checks/CheckEditor"

const ENV_OPTIONS = [
  { value: "prod", label: "Production" },
  { value: "stage", label: "Staging" },
  { value: "dev", label: "Development" },
]

const ENV_LABEL = { prod: "Production", stage: "Staging", dev: "Development" }

const DAY_OPTIONS = [
  { value: 1, label: "24 hours" },
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
]

function dateFilterToRange(df) {
  if (!df) return { days: 90, granularity: "day" }
  if (df.type === "custom" && df.range?.startDate && df.range?.endDate) {
    const from = new Date(df.range.startDate)
    const to = new Date(df.range.endDate)
    const days = Math.max(1, Math.ceil((to - from) / 86400000))
    const granularity = days <= 1 ? "hour" : "day"
    return { from: from.toISOString(), to: to.toISOString(), days, granularity }
  }
  const days = df?.value?.days ?? 90
  const now = new Date()
  if (days <= 1) {
    // Align to hour boundaries so this window agrees with the 24h uptime card
    // (server's read-component-uptime uses the same alignment).
    const HOUR = 60 * 60 * 1000
    const to = new Date(Math.floor(now.getTime() / HOUR) * HOUR + HOUR)
    const from = new Date(to.getTime() - days * 24 * HOUR)
    return { from: from.toISOString(), to: to.toISOString(), days, granularity: "hour" }
  }
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
  const startOfFirst = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1), 0, 0, 0, 0)
  return { from: startOfFirst.toISOString(), to: endOfToday.toISOString(), days, granularity: "day" }
}

const statusBadge = (uptime) => {
  if (uptime === null || uptime === undefined) return { label: "No data", color: "text-neutral-500" }
  if (uptime >= 0.999) return { label: "Operational", color: "text-green-600" }
  if (uptime >= 0.8) return { label: "Degraded", color: "text-amber-600" }
  return { label: "Down", color: "text-red-600" }
}

function padBuckets(buckets, expectedCount, stepMs) {
  // Align slots to step boundary (e.g. start of hour / minute) so the
  // current-hour KPI bucket matches the newest slot.
  const nowAligned = Math.floor(Date.now() / stepMs) * stepMs
  const slots = []
  for (let i = expectedCount - 1; i >= 0; i--) {
    slots.push(nowAligned - i * stepMs)
  }
  return slots.map((slotTs) => {
    const match = (buckets || []).find((b) => {
      const bt = Math.floor(new Date(b.timestamp).getTime() / stepMs) * stepMs
      return bt === slotTs
    })
    return match || { timestamp: new Date(slotTs).toISOString(), value: null }
  })
}

function latencyTimeline(buckets, heightPx = 100, expectedCount = 60, stepMs = 60_000) {
  const padded = padBuckets(buckets, expectedCount, stepMs)
  const max = Math.max(...padded.map((b) => b.value || 0), 1)
  return (
    <div className="flex items-end gap-px" style={{ height: heightPx }}>
      {padded.map((b, i) => (
        <div
          key={i}
          className={`flex-1 rounded-sm min-w-[2px] ${b.value == null ? "bg-canvas border border-bordered" : "bg-blue-500"}`}
          style={{ height: b.value != null ? `${Math.max((b.value / max) * 100, 2)}%` : "8%" }}
          title={`${new Date(b.timestamp).toLocaleTimeString()}: ${b.value != null ? Math.round(b.value) + "ms" : "no data"}`}
        />
      ))}
    </div>
  )
}

function uptimeBarColor(v) {
  if (v == null) return "bg-canvas border border-bordered"
  if (v >= 0.999) return "bg-green-500"
  if (v >= 0.8) return "bg-amber-500"
  return "bg-red-500"
}

function formatDowntime(v, stepMs) {
  if (v == null) return "no data"
  if (v >= 0.999) return "No Downtime"
  const downMs = Math.max(0, (1 - v) * stepMs)
  const totalMin = Math.round(downMs / 60_000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h > 0 && m > 0) return `${h}h ${m} min downtime`
  if (h > 0) return `${h}h downtime`
  return `${m} min downtime`
}

function uptimeTimeline(buckets, heightPx = 100, expectedCount = 24, stepMs = 3_600_000) {
  const padded = padBuckets(buckets, expectedCount, stepMs)
  return (
    <div className="flex items-stretch gap-px" style={{ height: heightPx }}>
      {padded.map((b, i) => (
        <div
          key={i}
          className={`flex-1 rounded-sm min-w-[2px] ${uptimeBarColor(b?.value)}`}
          title={`${new Date(b.timestamp).toLocaleString()} — ${formatDowntime(b?.value, stepMs)}`}
        />
      ))}
    </div>
  )
}

const ComponentDetail = ({ component, action, onAction, onDelete, dateFilter: dateFilterProp, onDateFilterChange }) => {
  const [checks, setChecks] = useState([])
  const [uptime, setUptime] = useState(null)
  const [latency, setLatency] = useState(null)
  const [uptimeHours, setUptimeHours] = useState([])
  const [latencyMin, setLatencyMin] = useState([])
  const [historyBuckets, setHistoryBuckets] = useState([])
  const [historyGranularity, setHistoryGranularity] = useState(null)
  const [localDateFilter, setLocalDateFilter] = useState({ type: "preset", value: { days: 90 } })
  const dateFilter = dateFilterProp || localDateFilter
  const setDateFilter = onDateFilterChange || setLocalDateFilter
  const [testing, setTesting] = useState(false)
  const [testingCheck, setTestingCheck] = useState({})
  const [addingCheck, setAddingCheck] = useState(false)
  const [editingCheck, setEditingCheck] = useState(null)
  const [doc, setDoc] = useState(component)
  const [tab, setTab] = useState(["details", "checks", "integrations"].includes(action) ? action : "stats")
  const [editName, setEditName] = useState(component?.name || "")
  const [editAppId, setEditAppId] = useState(component?.appId || "")
  const [editEnv, setEditEnv] = useState(ENV_OPTIONS.find((o) => o.value === (component?.environment || "prod")) || ENV_OPTIONS[0])
  const [editTags, setEditTags] = useState((component?.tags || []).join(", "))
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    setDoc(component)
    setEditName(component?.name || "")
    setEditAppId(component?.appId || "")
    setEditEnv(ENV_OPTIONS.find((o) => o.value === (component?.environment || "prod")) || ENV_OPTIONS[0])
    setEditTags((component?.tags || []).join(", "))
  }, [component?._id])

  useEffect(() => {
    const next = ["details", "checks", "integrations"].includes(action) ? action : "stats"
    if (next !== tab) setTab(next)
  }, [action])

  const onChangeTab = (v) => {
    setTab(v)
    if (onAction) onAction(v === "stats" ? null : v)
  }

  const onSave = async () => {
    setErr(null)
    setSaving(true)
    const r = await updateComponent(component._id, {
      name: editName,
      appId: editAppId,
      environment: editEnv?.value || editEnv,
      tags: editTags.split(",").map((t) => t.trim()).filter(Boolean),
    })
    setSaving(false)
    if (r?.error) return setErr(r.error)
    if (r?.component) setDoc(r.component)
  }

  const detailTabs = [
    { value: "stats", label: "Stats" },
    { value: "checks", label: "Checks" },
    { value: "details", label: "Details" },
    { value: "integrations", label: "Integrations" },
  ]

  const refresh = async () => {
    if (!component?._id) return
    const range = dateFilterToRange(dateFilter)
    // Send local TZ offset so the server can bucket by the user's local day for daily charts.
    range.tzOffsetMinutes = -new Date().getTimezoneOffset()
    const [u, l, h, c] = await Promise.all([
      readComponentUptime(component._id),
      readComponentLatency(component._id),
      readComponentHistory(component._id, range),
      listChecks({ componentId: component._id }),
    ])
    setUptime(u?.uptime ?? null)
    setUptimeHours(u?.hours || [])
    setLatency(l?.avgLatencyMs ?? null)
    setLatencyMin(l?.minuteBuckets || [])
    setHistoryBuckets(h?.buckets || [])
    setHistoryGranularity(h?.granularity || null)
    setChecks(c?.checks || [])
  }

  useEffect(() => { refresh() }, [component?._id, JSON.stringify(dateFilter)])

  // Live updates — refresh when any check of this component finishes.
  // Coalesce bursts into one refresh (trailing debounce).
  const liveRefreshTimer = useRef(null)
  useEffect(() => () => clearTimeout(liveRefreshTimer.current), [])
  useStatusUpdates({
    componentId: component?._id,
    onUpdate: () => {
      if (liveRefreshTimer.current) return
      liveRefreshTimer.current = setTimeout(() => {
        liveRefreshTimer.current = null
        refresh()
      }, 2000)
    },
  })

  const onDeleteCheck = async (c) => {
    const ok = await new Promise((resolve) => {
      showMessageDialog({
        title: "Delete check",
        message: `Delete check "${c.name || c.url}"? This cannot be undone.`,
        btnLabel: "Delete",
        onConfirm: () => resolve(true),
        onClose: () => resolve(false),
      })
    })
    if (!ok) return
    const r = await deleteCheck(c._id)
    if (r?.error) return setErr(r.error)
    refresh()
  }

  const onTestOne = async (c) => {
    setTestingCheck((s) => ({ ...s, [c._id]: true }))
    await testCheckNow(c._id)
    setTestingCheck((s) => ({ ...s, [c._id]: false }))
    refresh()
  }

  const onTestAll = async () => {
    setTesting(true)
    for (const c of checks) {
      await testCheckNow(c._id)
    }
    setTesting(false)
    refresh()
  }

  // "Current" state reflects the most-recent non-null hour bucket, not the
  // window average — a 24h dip should not flag the component as Degraded once
  // it has recovered.
  const currentUptime = (() => {
    const h = Array.isArray(uptimeHours) ? uptimeHours : []
    for (let i = h.length - 1; i >= 0; i--) {
      const v = h[i]?.value
      if (typeof v === "number") return v
    }
    return uptime
  })()
  const badge = statusBadge(currentUptime)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Typography variant="admin_h2">{doc?.name || component.name}</Typography>
          <div className="text-sm text-text_secondary">{ENV_LABEL[doc?.environment || component.environment] || (doc?.environment || component.environment)} · {((doc?.tags) || component.tags || []).join(", ")}</div>
        </div>
        {tab === "stats" && (
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={onTestAll} disabled={testing || checks.length === 0} className="whitespace-nowrap min-w-[140px]">
              {testing ? "Running all..." : "Test Now"}
            </Button>
            <DateFilterDropdown value={dateFilter} onChange={setDateFilter} showLabel={false} />
          </div>
        )}
      </div>

      <Tabs tabs={detailTabs} value={tab} onChange={onChangeTab} />

      {tab === "details" && (
        <div className="max-w-2xl space-y-4">
          <TextField label="Name" value={editName} onChange={setEditName} />
          <TextField label="App ID" value={editAppId} onChange={setEditAppId} placeholderText="e.g. aldoo-meet-web-app" />
          <DropdownSelect label="Environment" value={editEnv} onChange={(v) => setEditEnv(v?.[0] || v)} options={ENV_OPTIONS} />
          <TextField label="Tags (comma-separated)" value={editTags} onChange={setEditTags} />
          <div className="flex items-center gap-3">
            <Button onClick={onSave} disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
            {err && <div className="text-error text-sm">Error: {err}</div>}
          </div>

          {onDelete && (
            <div className="border-t border-bordered pt-4 mt-6">
              <Typography variant="admin_h4" className="mb-2 text-error">Danger zone</Typography>
              <div className="flex items-center justify-between gap-3 p-3 rounded border border-error/30 bg-error/5">
                <div className="text-sm text-text_secondary">
                  Delete this component and all of its checks and history. Cannot be undone.
                </div>
                <Button variant="secondary" onClick={onDelete} className="text-error border-error hover:bg-error/10 shrink-0">
                  Delete component
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "stats" && (<>

      {/* Status overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg border border-bordered bg-paper">
          <div className="text-xs text-text_secondary uppercase">Status</div>
          <div className={`text-lg font-bold mt-1 ${badge.color}`}>{badge.label}</div>
        </div>
        <div className="p-4 rounded-lg border border-bordered bg-paper">
          <div className="text-xs text-text_secondary uppercase">Uptime (24h)</div>
          <div className="text-lg font-bold mt-1">{uptime != null ? `${(uptime * 100).toFixed(2)}%` : "—"}</div>
        </div>
        <div className="p-4 rounded-lg border border-bordered bg-paper">
          <div className="text-xs text-text_secondary uppercase">Avg Latency (1h)</div>
          <div className="text-lg font-bold mt-1">{latency != null ? `${Math.round(latency)}ms` : "—"}</div>
        </div>
      </div>

      {/* Uptime grid */}
      <div className="p-4 rounded-lg border border-bordered bg-paper">
        <div className="flex items-center justify-between mb-2">
          <Typography variant="admin_h4">{dateFilterToRange(dateFilter).days}-day uptime</Typography>
          {(() => {
            const vals = (historyBuckets || []).map((b) => b?.value).filter((v) => v != null)
            if (vals.length === 0) return null
            const avg = vals.reduce((a, b) => a + b, 0) / vals.length
            const cls = avg >= 0.999 ? "text-green-600" : avg >= 0.8 ? "text-amber-600" : "text-red-600"
            return <span className={`text-sm font-semibold ${cls}`}>{(avg * 100).toFixed(2)}%</span>
          })()}
        </div>
        <HealthGrid componentId={component._id} days={dateFilterToRange(dateFilter).days} buckets={historyGranularity === "day" ? historyBuckets : undefined} />
      </div>

      {/* Hourly uptime bars */}
      <div className="p-4 rounded-lg border border-bordered bg-paper">
        <Typography variant="admin_h4" className="mb-2">Uptime — last 24 hours</Typography>
        {uptimeTimeline(uptimeHours, 80, 24, 3_600_000)}
        <div className="flex justify-between text-xs text-text_secondary mt-1">
          <span>24h ago</span>
          <span>now</span>
        </div>
        {(() => {
          const vals = (uptimeHours || []).map((b) => b?.value).filter((v) => v != null)
          if (vals.length === 0) return null
          const avg = vals.reduce((a, b) => a + b, 0) / vals.length
          return (
            <div className="flex justify-center text-xs text-text_secondary mt-2">
              <span><span className="font-medium text-text">avg uptime</span> {(avg * 100).toFixed(2)}%</span>
            </div>
          )
        })()}
      </div>

      {/* Latency trend */}
      <div className="p-4 rounded-lg border border-bordered bg-paper">
        <Typography variant="admin_h4" className="mb-2">Latency — last hour</Typography>
        {latencyTimeline(latencyMin, 80, 60, 60_000)}
        <div className="flex justify-between text-xs text-text_secondary mt-1">
          <span>60m ago</span>
          <span>now</span>
        </div>
        {(() => {
          const vals = (latencyMin || []).map((b) => b?.value).filter((v) => v != null)
          if (vals.length === 0) return null
          const min = Math.min(...vals)
          const max = Math.max(...vals)
          const avg = vals.reduce((a, b) => a + b, 0) / vals.length
          const latest = vals[vals.length - 1]
          return (
            <div className="flex justify-center gap-6 text-xs text-text_secondary mt-2">
              <span><span className="font-medium text-text">min</span> {Math.round(min)}ms</span>
              <span><span className="font-medium text-text">max</span> {Math.round(max)}ms</span>
              <span><span className="font-medium text-text">avg</span> {Math.round(avg)}ms</span>
              <span><span className="font-medium text-text">latest</span> {Math.round(latest)}ms</span>
            </div>
          )
        })()}
      </div>

      </>)}

      {tab === "checks" && (
        <div className="p-4 rounded-lg border border-bordered bg-paper">
          <div className="flex items-center justify-between mb-3">
            <Typography variant="admin_h4">Checks ({checks.length})</Typography>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={onTestAll} disabled={testing || checks.length === 0}>
                {testing ? "Running all..." : "Test all"}
              </Button>
              <Button onClick={() => setAddingCheck(true)}>+ Add Check</Button>
            </div>
          </div>

          <Modal
            isOpen={addingCheck}
            onClose={() => { setAddingCheck(false); refresh() }}
            title="New check"
            variant="compact"
            className="max-w-2xl"
            closeOnClickOutside
            closeOnEscape
          >
            <div className="p-4">
              <CheckEditor
                componentId={component._id}
                onClose={() => { setAddingCheck(false); refresh() }}
              />
            </div>
          </Modal>

          <Modal
            isOpen={!!editingCheck}
            onClose={() => { setEditingCheck(null); refresh() }}
            title="Edit check"
            variant="compact"
            className="max-w-2xl"
            closeOnClickOutside
            closeOnEscape
          >
            <div className="p-4">
              {editingCheck && (
                <CheckEditor
                  componentId={component._id}
                  check={editingCheck}
                  onClose={() => { setEditingCheck(null); refresh() }}
                />
              )}
            </div>
          </Modal>

          <div className="grid gap-2">
            {checks.map((c) => {
              const last = c.lastResult
              return (
                <div key={c._id} className="flex items-center justify-between gap-3 p-3 rounded border border-bordered bg-canvas">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{c.name || c.url}</div>
                    <div className="text-xs text-text_secondary">{c.kind} · every {c.schedule?.intervalSec}s</div>
                    {last && (
                      <div className={`text-xs mt-0.5 ${last.ok ? "text-green-600" : "text-red-600"}`}>
                        Last: {last.ok ? "OK" : "FAIL"} · {last.latencyMs != null ? `${last.latencyMs}ms` : ""}
                        {last.statusCode != null ? ` · HTTP ${last.statusCode}` : ""}
                        {last.diffPct != null ? ` · diff ${last.diffPct.toFixed(1)}%` : ""}
                        {last.errorType ? ` · ${last.errorType}` : ""}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="secondary" onClick={() => onTestOne(c)} disabled={!!testingCheck[c._id]}>
                      {testingCheck[c._id] ? "Running..." : "Run Test"}
                    </Button>
                    <button
                      onClick={() => setEditingCheck(c)}
                      className="p-2 rounded hover:bg-primary/10 text-primary transition-colors"
                      title="Edit check"
                    >
                      <PencilSquareIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => onDeleteCheck(c)}
                      className="p-2 rounded hover:bg-error/10 text-error transition-colors"
                      title="Delete check"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {tab === "integrations" && (
        <IntegrationsPanel componentId={component._id} projectId={component.projectId} />
      )}
    </div>
  )
}

const REMOTE_STATUS_KEY_NAME = "Remote Status Access"

function IntegrationsPanel({ componentId, projectId }) {
  // The stats endpoint must hit the API host, not the web app origin —
  // /v1/status/component/:id is mounted on the API.
  const origin = typeof window !== "undefined" ? window.location.origin : ""
  const base = (appConfig?.PLATFORM_API_URL || origin || "").replace(/\/$/, "")
  const endpoint = `${base}/v1/status/component/${componentId}`
  const [auth] = PlatformAuth.useAuth()
  const [keys, setKeys] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [genErr, setGenErr] = useState(null)

  const userId = auth?.userId || auth?.user?._id || auth?._id

  const refreshKeys = async () => {
    if (!userId) return
    const r = await APIKeysPlatformAPI.ListAPIKeys({ userId, status: "active", limit: 100 })
    setKeys(r?.keys || [])
  }
  useEffect(() => { refreshKeys() }, [userId])

  // One shared per-user "Remote Status Access" key — same key shown across all
  // projects/components the user can see. Identified by description, not by
  // project binding (which the stats endpoint no longer requires).
  const remoteKey = (keys || []).find(
    (k) => (k?.description || "") === REMOTE_STATUS_KEY_NAME
  )
  const visibleKey = remoteKey?.key || null

  const onGenerate = async () => {
    if (!userId) return
    setGenErr(null)
    setGenerating(true)
    try {
      const created = await APIKeysPlatformAPI.CreateAPIKey({
        targetUserId: userId,
        description: REMOTE_STATUS_KEY_NAME,
      })
      if (!created?.result) { setGenErr(created?.error || "create_failed"); return }
      await refreshKeys()
    } catch (e) {
      setGenErr(e.message || "error")
    } finally {
      setGenerating(false)
    }
  }

  const copy = (text) => { try { navigator.clipboard.writeText(text) } catch {} }
  const curlKey = visibleKey || "<YOUR_API_KEY>"
  const curl = `curl -X POST '${endpoint}' \\
  -H 'aldoo-api-auth-key: ${curlKey}' \\
  -H 'Content-Type: application/json' \\
  -d '{"days":1}'`

  return (
    <div className="space-y-4 max-w-3xl">

      {/* API key section */}
      <div className="p-4 rounded-lg border border-bordered bg-paper space-y-3">
        <Typography variant="admin_h4">Your Remote Status Access key</Typography>
        {visibleKey ? (
          <>
            <div className="text-sm text-text_secondary">
              Single per-user key. Works on every component in any project you can access.
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 rounded bg-canvas border border-bordered text-sm break-all">{visibleKey}</code>
              <Button variant="secondary" onClick={() => copy(visibleKey)}>Copy</Button>
            </div>
          </>
        ) : keys === null ? (
          <div className="text-sm text-text_secondary">Loading…</div>
        ) : (
          <>
            <div className="text-sm text-text_secondary">
              No Remote Status Access key yet. Generate one — it'll be reused for every component.
            </div>
            <Button onClick={onGenerate} disabled={generating || !userId}>
              {generating ? "Generating…" : "Generate API key"}
            </Button>
            {genErr && <div className="text-error text-sm">Error: {genErr}</div>}
          </>
        )}
      </div>

      <div className="p-4 rounded-lg border border-bordered bg-paper space-y-3">
        <Typography variant="admin_h4">Stats endpoint</Typography>
        <div className="text-sm text-text_secondary">
          External services can POST to this URL with the API key above to fetch uptime + latency stats.
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 px-3 py-2 rounded bg-canvas border border-bordered text-sm break-all">{endpoint}</code>
          <Button variant="secondary" onClick={() => copy(endpoint)}>Copy</Button>
        </div>
      </div>

      <div className="p-4 rounded-lg border border-bordered bg-paper space-y-2">
        <Typography variant="admin_h4">Authentication</Typography>
        <div className="text-sm text-text_secondary">
          Header <code className="px-1.5 py-0.5 rounded bg-canvas border border-bordered">aldoo-api-auth-key: &lt;YOUR_API_KEY&gt;</code>. The key works for any component in projects your account can access.
        </div>
      </div>

      <div className="p-4 rounded-lg border border-bordered bg-paper space-y-2">
        <Typography variant="admin_h4">Body</Typography>
        <div className="text-sm text-text_secondary">
          Optional JSON body: <code className="px-1.5 py-0.5 rounded bg-canvas border border-bordered">{`{"days": 1, "granularity": "hour" | "day"}`}</code>.
          Defaults: <code className="px-1.5 py-0.5 rounded bg-canvas border border-bordered">days=1, granularity=hour</code> (auto: day when range &gt; 1 day).
        </div>
      </div>

      <div className="p-4 rounded-lg border border-bordered bg-paper space-y-2">
        <Typography variant="admin_h4">Example</Typography>
        <pre className="px-3 py-2 rounded bg-canvas border border-bordered text-xs overflow-x-auto whitespace-pre-wrap">{curl}</pre>
        <Button variant="secondary" onClick={() => copy(curl)}>Copy curl</Button>
      </div>

      <div className="p-4 rounded-lg border border-bordered bg-paper space-y-2">
        <Typography variant="admin_h4">Response shape</Typography>
        <pre className="px-3 py-2 rounded bg-canvas border border-bordered text-xs overflow-x-auto whitespace-pre">{`{
  "result": true,
  "componentId": "...",
  "projectId": "...",
  "name": "...",
  "appId": "...",
  "environment": "prod",
  "days": 1,
  "granularity": "hour",
  "from": "ISO",
  "to": "ISO",
  "uptime": 0.9896,
  "uptimePct": "98.96",
  "avgLatencyMs": 102,
  "history": [{ "timestamp": "ISO", "value": 1 }, ...]
}`}</pre>
      </div>
    </div>
  )
}

export default ComponentDetail
